import React, { useState, useEffect, FC } from 'react';
import { Link } from 'react-router-dom';
import {
  Autocomplete,
  TextField,
  InputAdornment,
  Typography,
} from '@mui/material';
import { gql, useLazyQuery } from '@apollo/client';
import {
  VenueSearchQuery,
  VenueSearchQueryVariables,
} from '@pv/common/graphql/operations';
import { Search } from 'react-feather';
import { PVBlack, PVGray } from '@pv/common/colors';

const venueSearchQuery = gql`
  query VenueSearch($query: String!) {
    marketplaceVenueSearch(query: $query) {
      id
      name
      slug
      address {
        id
        city
        state
      }
    }
  }
`;

export const VenueSearch: FC = () => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<
    {
      id: string;
      name: string;
      slug: string;
      address: {
        city: string;
        state: string;
      };
    }[]
  >([]);

  const [searchItems, { loading, data }] = useLazyQuery<
    VenueSearchQuery,
    VenueSearchQueryVariables
  >(venueSearchQuery);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (inputValue.trim() !== '') {
        searchItems({ variables: { query: inputValue } });
      } else {
        setOptions([]);
      }
    }, 300);

    return () => clearTimeout(handler);
  }, [inputValue, searchItems]);

  useEffect(() => {
    if (data?.marketplaceVenueSearch) {
      setOptions(
        data.marketplaceVenueSearch.map((result) => ({
          id: result.id,
          name: result.name,
          slug: result.slug,
          address: {
            city: result.address?.city ?? '',
            state: result.address?.state ?? '',
          },
        })),
      );
    }
  }, [data]);

  return (
    <Autocomplete
      freeSolo
      options={options}
      loading={loading}
      onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : (option?.name ?? '')
      }
      sx={{
        flex: 1,
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          size="small"
          placeholder="Search venue"
          inputProps={{
            ...params.inputProps,
            'aria-label': 'Search venue',
          }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment
                position="start"
                sx={{ color: PVBlack, ml: 1, mr: 0.5 }}
              >
                <Search size={20} />
              </InputAdornment>
            ),
            sx: {
              borderRadius: '500px',
              '& fieldset': {
                borderRadius: '500px',
              },
            },
          }}
          sx={{
            '& .MuiInputLabel-root': {
              display: 'none',
            },
          }}
        />
      )}
      renderOption={(props, option) => (
        <li {...props}>
          <Link
            to={`/venues/${option.slug}`}
            target="_blank"
            style={{ textDecoration: 'none', color: 'inherit', width: '100%' }}
          >
            <Typography variant="body1">{option.name}</Typography>
            <Typography variant="body2" color={PVGray}>
              {option.address.city}, {option.address.state}
            </Typography>
          </Link>
        </li>
      )}
    />
  );
};
